import React, {useEffect, useMemo, useState} from 'react';

import isEmpty from 'lodash.isempty';
import {Separator} from 'src/components/separator';
import {useAttributeUsage} from 'src/hooks/useAttributeUsage';
import {getAttributeIds} from 'src/modules/event-create/attributes-block/utils/getAttributeIds';
import {ATTRIBUTES_INFO} from 'src/modules/event-create/event-create-hovers';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';

import {Dropdown} from '@pb/kit-component-dropdown-v2';
import {Group, Item} from '@pb/kit-component-group';
import {Icons, Sizes} from '@pb/kit-component-icon';
import {BasicIconButton, InfoIconButton} from '@pb/kit-component-icon-button';
import {Loading} from '@pb/kit-component-loading';
import {Tooltip} from '@pb/kit-component-popups';
import {Typography} from '@pb/kit-component-typography';

import {EventCreationMode} from '../constants';
import {CreationAttribute} from '../typedefs';

import {AttributeFormRow} from './attribute-form/attribite-form-row';
import {AttributeFormHeader} from './attribute-form/attribute-form-header';
import {AttributeTemplate} from './attribute-template/attribute-template';

type Props = {
    attributes?: Array<CreationAttribute>,
    mode: EventCreationMode,
    validationHighLight: boolean,
    addedAttributes: CreationAttribute[],
    template: Array<CreationAttribute>,
    changeRequestId: number | null,
    eventChangeRequest: EventChangeRequest,
    isLoadingAttributes: boolean,
    isEventMarkedDeprecated: boolean,
    onAdd: () => void,
    onRemove?: (removeAttribute: CreationAttribute) => void,
    onChangeAttribute: (changedAttribute: CreationAttribute) => void,
};

const MIN_ATTRIBUTES_COUNT = 1;

export const AttributesBlockView = (props: Props) => {
    const {
        attributes,
        addedAttributes,
        template,
        changeRequestId,
        eventChangeRequest,
        isLoadingAttributes,
        isEventMarkedDeprecated,
        mode,
        validationHighLight,
        onAdd,
        onChangeAttribute,
        onRemove,
    } = props;

    const [isDeleteDisabled, setIsDeleteDisabled] = useState(false);
    const attributesIds = useMemo(() => getAttributeIds(attributes), [attributes]);
    const attributeUsages = useAttributeUsage(attributesIds);

    useEffect(() => {
        setIsDeleteDisabled(
            mode === EventCreationMode.create
            && template.length === 0
            && addedAttributes.length === MIN_ATTRIBUTES_COUNT,
        );
    }, [addedAttributes.length, mode, template.length]);

    return (
        <Group direction="v" gap="m">
            <Item style={{width: '100%'}}>
                <Group gap="xs">
                    <Typography variant="heading-level-4" fontWeight="semi-bold">Attributes:</Typography>
                    <Dropdown
                        hoverEnabled={true}
                        clickEnabled={false}
                        maxWidth={500}
                        anchor={({anchorRef, getAnchorProps}) => (
                            <InfoIconButton {...getAnchorProps()} ref={anchorRef}/>
                        )}>
                        {ATTRIBUTES_INFO}
                    </Dropdown>
                </Group>
                <Separator/>
            </Item>
            <Item style={{width: '100%'}}>
                <Group direction="v" gap="m">
                    <Item style={{width: '100%'}}>
                        <Group direction="v" gap="xs">
                            <AttributeFormHeader mode={mode} changeRequestId={changeRequestId}/>
                            {isLoadingAttributes ? <Loading fast={true}/> : null}
                            {mode === EventCreationMode.create && !isEmpty(template) && !isLoadingAttributes ? (
                                <Item style={{width: '100%'}}>
                                    <AttributeTemplate template={template}/>
                                </Item>
                            ) : null}
                            {mode === EventCreationMode.update && attributes && !isLoadingAttributes ?
                                    attributes.map(attribute => (
                                        <AttributeFormRow
                                            key={attribute.id}
                                            {...{
                                                mode,
                                                attribute,
                                                changeRequestId,
                                                eventChangeRequest,
                                                attributeUsages,
                                                validationHighLight,
                                                onChangeAttribute,
                                                isEventMarkedDeprecated,
                                            }}
                                        />
                                    )) : null}
                            {addedAttributes.length && !isLoadingAttributes ?
                                    addedAttributes.map(attribute => (
                                        <AttributeFormRow
                                            key={attribute.attributeId}
                                            {...{
                                                mode,
                                                addedAttribute: attribute,
                                                changeRequestId,
                                                eventChangeRequest,
                                                isDeleteDisabled,
                                                isEventMarkedDeprecated,
                                                validationHighLight,
                                                onRemove,
                                                onChangeAttribute,
                                            }}
                                        />
                                    )) : null}
                        </Group>
                    </Item>
                    <Group justify="start">
                        <Tooltip
                            anchor={({anchorRef}) => (
                                <BasicIconButton
                                    ref={anchorRef}
                                    iconProps={{type: Icons.PLUS, size: Sizes.Size_19}}
                                    onClick={onAdd}
                                />
                            )}>
                            Add Attribute
                        </Tooltip>
                    </Group>
                </Group>
            </Item>
        </Group>
    );
};
